import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  MapPin, ArrowRight, Check, Users, Home, Key, 
  Calendar, FileText, Navigation, History, Sparkles, MessageSquare
} from 'lucide-react';

const PropertyManagementPage = () => {
  const features = [
    {
      icon: Users,
      title: "Customer Profiles",
      description: "Store complete customer information including contact details, billing addresses, and notes. Keep everything organized in one place."
    },
    {
      icon: Home,
      title: "Multiple Properties",
      description: "Each customer can have unlimited properties. Perfect for customers with multiple locations or property management companies."
    },
    {
      icon: FileText,
      title: "Service Instructions",
      description: "Add detailed service instructions for each property. Crews see exactly what needs to be done before they arrive."
    },
    {
      icon: Key,
      title: "Access Information",
      description: "Store gate codes, lock box combinations, and special access notes. Never leave a crew stranded at a locked gate."
    },
    {
      icon: Navigation,
      title: "Map Integration",
      description: "Properties are mapped with GPS coordinates for easy navigation and route optimization."
    },
    {
      icon: Calendar,
      title: "Preferred Service Days",
      description: "Track which days each customer prefers service. Make scheduling decisions that keep customers happy."
    },
    {
      icon: History,
      title: "Service History",
      description: "View complete service history for every property. See what was done, when, and by which crew."
    }
  ];

  const benefits = [
    "Eliminate paper files and scattered spreadsheets",
    "Give crews instant access to property details on mobile",
    "Track service history for better customer relationships",
    "Never forget special instructions or access codes",
    "Easily transfer properties between routes or crews"
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MapPin className="h-4 w-4" />
                Core Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Customer & Property Management
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                The foundation of your landscaping operation. Store everything you need to know about 
                every customer and property in one organized, accessible system.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1764969370921-7c8df49171cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtYW5pY3VyZWQlMjBsYXduJTIwYmFja3lhcmQlMjBncmVlbiUyMGdyYXNzfGVufDB8fHx8MTc3Mzc5NDY5M3ww&ixlib=rb-4.1.0&q=85"
                alt="Beautiful manicured lawn"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything in One Place</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stop juggling spreadsheets, paper files, and sticky notes. Orchard LMS keeps all your 
              customer and property data organized and accessible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-gray-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Property-Centric Matters
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Landscaping businesses don't just serve customers—they service specific properties 
                with unique needs. Orchard LMS is built around this reality.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-primary/5 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Quick Stats</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">500+</p>
                    <p className="text-sm text-gray-500">Properties</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">250</p>
                    <p className="text-sm text-gray-500">Customers</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">100%</p>
                    <p className="text-sm text-gray-500">Organized</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-primary">0</p>
                    <p className="text-sm text-gray-500">Paper Files</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Find Customer Info Instantly
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our AI Assistant knows everything about your customers and properties. Just ask, 
                and get instant answers—no digging through records required.
              </p>
              <ul className="space-y-4">
                {[
                  "Ask about any customer's details",
                  "Look up property service history",
                  "Find customers by service type",
                  "Get gate codes and special instructions"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"Tell me about the Johnson account"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">Here's the Johnson account info:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200 space-y-2">
                    <p className="text-sm"><span className="font-medium">Customer:</span> Robert Johnson</p>
                    <p className="text-sm"><span className="font-medium">Properties:</span> 2 active</p>
                    <p className="text-sm"><span className="font-medium">Services:</span> Weekly mowing, Monthly hedge trim</p>
                    <p className="text-sm"><span className="font-medium">Since:</span> March 2022</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">Want details on a specific property?</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Get Your Properties Organized
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start with a free account and import your customer list. You'll wonder how you ever managed without it.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default PropertyManagementPage;
